// @flow
import { defineMessages } from 'react-intl';

import {
    circulateDurations,
    deviceModes,
    deviceModels,
    deviceTypes,
    fanModes,
    manufacturers,
    gu10Models,
    candlelightModels
} from '~/core/constants/devices';

export const deviceModeMessages = defineMessages({
    [deviceModes.BOOST]: {
        id: 'device.modes.boost',
        defaultMessage: 'Heating'
    },
    [deviceModes.MANUAL]: {
        id: 'device.modes.manual',
        defaultMessage: 'Manual'
    },
    [deviceModes.OFF]: {
        id: 'device.modes.off',
        defaultMessage: 'Off'
    },
    [deviceModes.ON]: {
        id: 'device.modes.on',
        defaultMessage: 'On'
    },
    [deviceModes.SCHEDULE]: {
        id: 'device.modes.schedule',
        defaultMessage: 'Schedule'
    },
    [deviceModes.HOLD]: {
        id: 'device.modes.hold',
        defaultMessage: 'Hold'
    },
    [deviceModes.MIMIC]: {
        id: 'device.modes.mimic',
        defaultMessage: 'Mimic'
    }
});

export const fanModeMessages = defineMessages({
    [fanModes.AUTO]: {
        id: 'device.fanmodes.auto',
        defaultMessage: 'Auto'
    },
    [fanModes.CIRCULATE]: {
        id: 'device.fanmodes.circulate',
        defaultMessage: 'Circulate'
    },
    [fanModes.ALWAYS]: {
        id: 'device.fanmodes.always',
        defaultMessage: 'Always on'
    }
});

export const circulateDurationMessages = defineMessages({
    [circulateDurations.FIFTEEN]: {
        id: 'device.circulate-durations.fifteen',
        defaultMessage: '15Mins'
    },
    [circulateDurations.THIRTY]: {
        id: 'device.circulate-durations.thirty',
        defaultMessage: '30Mins'
    },
    [circulateDurations.FORTYFIVE]: {
        id: 'device.circulate-durations.fortyfive',
        defaultMessage: '45Mins'
    },
    description: {
        id: 'device.circulate-durations.description',
        defaultMessage: 'every hour'
    }
});

export const deviceOnlineMessages = defineMessages({
    connected: {
        id: 'device.online.connected',
        defaultMessage: 'Connected'
    },
    off: {
        id: 'devices.off',
        defaultMessage: 'off'
    },
    offline: {
        id: 'device.online.offline',
        defaultMessage: 'Offline'
    },
    on: {
        id: 'devices.on',
        defaultMessage: 'on'
    },
    identify: {
        id: 'devices.identify',
        defaultMessage: 'Flash Light'
    }
});

export const deviceTypeMessages = defineMessages({
    [deviceTypes.ACTIVE_PLUG]: {
        id: 'device.types.activeplug',
        defaultMessage: `{amount, plural,
			one {Active Plug}
			other {Active Plugs}
		}`
    },
    [deviceTypes.BOILER_MODULE]: {
        id: 'device.types.boilermodule',
        defaultMessage: `{amount, plural,
			one {Receiver}
			other {Receivers}
		}`
    },
    [deviceTypes.BULB]: {
        id: 'device.types.colourtuneablelight',
        defaultMessage: `{amount, plural,
			one {Colour Light}
			other {Colour Lights}
		}`
    },
    [deviceTypes.COLOR_TUNEABLE_LIGHT]: {
        id: 'device.types.colourtuneablelight',
        defaultMessage: `{amount, plural,
			one {Colour Light}
			other {Colour Lights}
		}`
    },
    [deviceTypes.COLOR_LIGHT]: {
        id: 'device.types.colourlight',
        defaultMessage: `{amount, plural,
			one {Colour Light}
			other {Colour Lights}
		}`
    },
    [deviceTypes.CONTACT_SENSOR]: {
        id: 'device.types.contactsensor',
        defaultMessage: `{amount, plural,
			one {Window or Door Sensor}
			other {Window or Door Sensors}
		}`
    },
    [deviceTypes.HEATING]: {
        id: 'device.types.heating',
        defaultMessage: `{amount, plural,
			one {Heating Module}
			other {Heating Modules}
		}`
    },
    [deviceTypes.HOT_WATER]: {
        id: 'device.types.hotwater',
        defaultMessage: `{amount, plural,
			one {Hot Water Module}
			other {Hot Water Modules}
		}`
    },
    [deviceTypes.HIVE_BUTTON]: {
        id: 'device.types.hivebutton',
        defaultMessage: `{amount, plural,
			one {Hive Button}
			other {Hive Buttons}
		}`
    },
    [deviceTypes.HIVE_CAMERA]: {
        id: 'device.types.hivecamera',
        defaultMessage: `{amount, plural,
			one {Camera}
			other {Cameras}
		}`
    },
    [deviceTypes.HUB]: {
        id: 'device.types.hub',
        defaultMessage: `{amount, plural,
			one {Hub}
			other {Hubs}
		}`
    },
    [`${deviceTypes.HUB}-${deviceModels.SENSE}`]: {
        id: 'device.types.hub.sense',
        defaultMessage: `{amount, plural,
			one {Hive Hub 360}
			other {Hive Hub 360}
		}`
    },
    [deviceTypes.HUE_BRIDGE]: {
        id: 'device.types.philipshuebridge',
        defaultMessage: `{amount, plural,
			one {Philips Hue Bridge}
			other {Philips Hue Bridges}
		}`
    },
    [deviceTypes.LEAK_SENSOR]: {
        id: 'device.types.leaksensor',
        defaultMessage: `{amount, plural,
        one {Leak Sensor}
        other {Leak Sensors}

      }`
    },
    [deviceTypes.MOTION_SENSOR]: {
        id: 'device.types.motionsensor',
        defaultMessage: `{amount, plural,
			one {Motion Sensor}
			other {Motion Sensors}
		}`
    },
    [deviceTypes.NA_THERMOSTAT]: {
        id: 'device.types.nathermostat',
        defaultMessage: `{amount, plural,
			one {Thermostat}
			other {Thermostat}
		}`
    },
    [deviceTypes.SENSE]: {
        id: 'device.types.sense',
        defaultMessage: `{amount, plural,
			one {Hive Hub 360}
			other {Hive Hub 360}
		}`
    },
    [deviceTypes.SIGNAL_BOOSTER]: {
        id: 'device.types.signalbooster',
        defaultMessage: `{amount, plural,
			one {Signal Booster}
			other {Signal Boosters}
		}`
    },
    [deviceTypes.SMART_PLUG]: {
        id: 'device.types.smartplug',
        defaultMessage: `{amount, plural,
			one {Smart Plug}
			other {Smart Plugs}
		}`
    },
    [deviceTypes.THERMOSTAT_UI]: {
        id: 'device.types.thermostatui',
        defaultMessage: `{amount, plural,
			one {Thermostat}
			other {Thermostats}
		}`
    },
    [deviceTypes.THERMOSTAT]: {
        id: 'device.types.thermostatui',
        defaultMessage: `{amount, plural,
			one {Thermostat}
			other {Thermostats}
		}`
    },
    [deviceTypes.TUNEABLE_LIGHT]: {
        id: 'device.types.tuneablelight',
        defaultMessage: `{amount, plural,
			one {Tuneable Light}
			other {Tuneable Lights}
		}`
    },
    [deviceTypes.WARM_WHITE_LIGHT]: {
        id: 'device.types.warmwhitelight',
        defaultMessage: `{amount, plural,
			one {Warm White Light}
			other {Warm White Lights}
		}`
    },
    // all Hue lights translations are the same; unfortunately yarn extract-messages doesn't support referenced translations
    [`${deviceTypes.COLOR_TUNEABLE_LIGHT}-${manufacturers.PHILIPS}`]: {
        id: 'device.types.huelight',
        defaultMessage: `{amount, plural,
          one {Hue Light}
          other {Hue Lights}
        }`
    },
    [`${deviceTypes.COLOR_LIGHT}-${manufacturers.PHILIPS}`]: {
        id: 'device.types.huelight',
        defaultMessage: `{amount, plural,
          one {Hue Light}
          other {Hue Lights}
        }`
    },
    [`${deviceTypes.TUNEABLE_LIGHT}-${manufacturers.PHILIPS}`]: {
        id: 'device.types.huelight',
        defaultMessage: `{amount, plural,
          one {Hue Light}
          other {Hue Lights}
        }`
    },
    [`${deviceTypes.WARM_WHITE_LIGHT}-${manufacturers.PHILIPS}`]: {
        id: 'device.types.huelight',
        defaultMessage: `{amount, plural,
          one {Hue Light}
          other {Hue Lights}
        }`
    }
});

export const deviceModelTypeMessages = defineMessages({
    [gu10Models.FWBULB01UK]: {
        id: 'device.types.gu10.white',
        defaultMessage: `{amount, plural,
            one {Warm White Spotlight}
            other {Warm White Spotlights}
        }`
    },
    [gu10Models.FWGU10BULBUK]: {
        id: 'device.types.gu10.white',
        defaultMessage: `{amount, plural,
            one {Warm White Spotlight}
            other {Warm White Spotlights}
        }`
    },
    [gu10Models.FWGU10BULB01UK]: {
        id: 'device.types.gu10.white',
        defaultMessage: `{amount, plural,
            one {Warm White Spotlight}
            other {Warm White Spotlights}
        }`
    },
    [gu10Models.TWGU10BULB01UK]: {
        id: 'device.types.gu10.tuneable',
        defaultMessage: `{amount, plural,
            one {Tuneable Spotlight}
            other {Tuneable Spotlights}
        }`
    },
    [candlelightModels.FWCLBulb01UK]: {
        id: 'device.types.candlelight.white',
        defaultMessage: `{amount, plural,
            one {Warm White Candlelight}
            other {Warm White Candlelights}
        }`
    },
    [candlelightModels.TWCLBulb01UK]: {
        id: 'device.types.candlelight.tuneable',
        defaultMessage: `{amount, plural,
            one {Tuneable Candlelight}
            other {Tuneable Candlelights}
        }`
    }
});



// WEBPACK FOOTER //
// ./src/core/translations/devices/intl.js